import {
  DOM_EVENT_SPRINT_PLAN_BOARD,
  LS_SPRINT_PLAN_DEFAULT_BOARD_ID,
  LS_SPRINT_PLAN_ACTIVE_BOARD_ID,
} from '../config/storageKeys';

/** Fired after Sprint Plan writes board selection; `detail.productId` scopes same-tab listeners. */
export const SPRINT_PLAN_BOARD_CHANGED_EVENT = DOM_EVENT_SPRINT_PLAN_BOARD;

export function sprintPlanDefaultBoardKey(productId: string): string {
  return `${LS_SPRINT_PLAN_DEFAULT_BOARD_ID}.${productId}`;
}

export function sprintPlanActiveBoardKey(productId: string): string {
  return `${LS_SPRINT_PLAN_ACTIVE_BOARD_ID}.${productId}`;
}

/** Saved default from "Save as default board"; falls back to pre–per-product legacy key. */
export function readSprintPlanDefaultBoardId(productId: string | null | undefined): string | null {
  if (typeof localStorage === 'undefined') return null;
  if (productId) {
    const scoped = localStorage.getItem(sprintPlanDefaultBoardKey(productId));
    if (scoped?.trim()) return scoped.trim();
  }
  const legacy = localStorage.getItem(LS_SPRINT_PLAN_DEFAULT_BOARD_ID);
  return legacy?.trim() || null;
}

/**
 * Current board `<select>` in Sprint Plan; falls back to default (per product), then legacy active, then default again.
 */
export function readSprintPlanActiveBoardId(productId: string | null | undefined): string | null {
  if (typeof localStorage === 'undefined') return null;
  if (productId) {
    const scoped = localStorage.getItem(sprintPlanActiveBoardKey(productId));
    if (scoped?.trim()) return scoped.trim();
  } else {
    const legacyActive = localStorage.getItem(LS_SPRINT_PLAN_ACTIVE_BOARD_ID);
    if (legacyActive?.trim()) return legacyActive.trim();
  }
  return readSprintPlanDefaultBoardId(productId);
}

export function writeSprintPlanDefaultBoardId(
  productId: string | null | undefined,
  boardId: string
): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (productId) {
      localStorage.setItem(sprintPlanDefaultBoardKey(productId), boardId);
    } else {
      localStorage.setItem(LS_SPRINT_PLAN_DEFAULT_BOARD_ID, boardId);
    }
  } catch {
    // ignore
  }
}

export function writeSprintPlanActiveBoardId(
  productId: string | null | undefined,
  boardId: string
): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (productId) {
      localStorage.setItem(sprintPlanActiveBoardKey(productId), boardId);
    } else {
      localStorage.setItem(LS_SPRINT_PLAN_ACTIVE_BOARD_ID, boardId);
    }
  } catch {
    // ignore
  }
}

export function shouldSyncSprintPlanStorageKey(
  key: string | null,
  productId: string | null | undefined
): boolean {
  if (!key) return false;
  if (productId) {
    return (
      key === sprintPlanDefaultBoardKey(productId) || key === sprintPlanActiveBoardKey(productId)
    );
  }
  return key === LS_SPRINT_PLAN_DEFAULT_BOARD_ID || key === LS_SPRINT_PLAN_ACTIVE_BOARD_ID;
}
